import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast } from '@src/components/ui';
import { useAuth } from './AuthContext';
import { getCart, addToCart as addToCartApi, removeFromCart as removeFromCartApi, updateCartItemQuantity as updateCartItemQuantityApi, clearCart as clearCartApi, migrateGuestCartToUser, } from '../utils/cartStorage';
import { useImpersonation } from './ImpersonationContext';
const CartContext = createContext(undefined);
export const CartProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const { showToast } = useToast();
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { isImpersonating } = useImpersonation();
    const refresh = useCallback(async () => {
        if (isLoading)
            return;
        setIsLoading(true);
        try {
            const cartItems = await getCart(isAuthenticated);
            setItems(cartItems);
        }
        catch (err) {
            console.error('Failed to refresh cart:', err);
            showToast({
                message: 'Failed to refresh cart. Please try again.',
                variant: 'error',
                noBorder: true,
            });
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, showToast]);
    const addItem = useCallback(async (item) => {
        if (isLoading)
            return;
        setIsLoading(true);
        try {
            await addToCartApi(item, isAuthenticated);
            const cartItems = await getCart(isAuthenticated);
            setItems(cartItems);
        }
        catch (err) {
            console.error('Failed to add item to cart:', err);
            showToast({
                message: 'Failed to add item to cart. Please try again.',
                variant: 'error',
                noBorder: true,
            });
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, showToast]);
    const removeItem = useCallback(async (productId) => {
        if (isLoading)
            return;
        setIsLoading(true);
        try {
            await removeFromCartApi(productId, isAuthenticated);
            const cartItems = await getCart(isAuthenticated);
            setItems(cartItems);
        }
        catch (err) {
            console.error('Failed to remove item from cart:', err);
            showToast({
                message: 'Failed to remove item from cart. Please try again.',
                variant: 'error',
                noBorder: true,
            });
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, showToast]);
    const updateQuantity = useCallback(async (productId, quantity) => {
        if (isLoading)
            return;
        setIsLoading(true);
        setItems((prev) => prev.map((item) => (item.id === productId ? { ...item, quantity } : item)));
        try {
            await updateCartItemQuantityApi(productId, quantity, isAuthenticated);
        }
        catch (err) {
            console.error('Failed to update cart item:', err);
            showToast({
                message: 'Failed to update cart item. Please try again.',
                variant: 'error',
                noBorder: true,
            });
            const cartItems = await getCart(isAuthenticated);
            setItems(cartItems);
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, showToast]);
    const clearAll = useCallback(async () => {
        if (isLoading)
            return;
        setIsLoading(true);
        try {
            await clearCartApi(isAuthenticated);
            setItems([]);
        }
        catch (err) {
            console.error('Failed to clear cart:', err);
            showToast({
                message: 'Failed to clear cart. Please try again.',
                variant: 'error',
                noBorder: true,
            });
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, isLoading, showToast]);
    useEffect(() => {
        const loadCart = async () => {
            setIsLoading(true);
            try {
                if (isAuthenticated) {
                    const localCart = localStorage.getItem('cartItems');
                    if (localCart && JSON.parse(localCart).length > 0) {
                        await migrateGuestCartToUser(isAuthenticated);
                    }
                }
                const cartItems = await getCart(isAuthenticated);
                setItems(cartItems);
            }
            catch (err) {
                console.error('Failed to initialize cart:', err);
                showToast({
                    message: 'Failed to load cart. Please refresh the page.',
                    variant: 'error',
                    noBorder: true,
                });
            }
            finally {
                setIsLoading(false);
            }
        };
        loadCart();
    }, [isAuthenticated, isImpersonating, showToast]);
    const value = {
        items,
        isLoading,
        addItem,
        removeItem,
        updateQuantity,
        clearAll,
        refresh,
    };
    return _jsx(CartContext.Provider, { value: value, children: children });
};
export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
